import React, { useState } from "react";

interface OcPortalConfig {
  tenantName: string;
  branding: {
    logoUrl?: string;
    primaryColor?: string;
    supportContactText?: string;
  };
  allowedChannels: { email: boolean; sms: boolean };
  turnstileSiteKey: string;
  supportText: string | null;
}

interface OcStepDoneProps {
  config: OcPortalConfig;
}

/**
 * Wipe every client-side trace of this reset session: localStorage,
 * sessionStorage, all same-origin cookies (path/domain variants), and
 * the IndexedDB databases under this origin. Public-kiosk-safe cleanup.
 */
async function ocClearLocalState(): Promise<void> {
  // 1. Storage APIs.
  try { window.localStorage.clear(); } catch { /* private/disabled */ }
  try { window.sessionStorage.clear(); } catch { /* private/disabled */ }

  // 2. Cookies. document.cookie can only see HTTP-only=false cookies on
  //    the current path/domain. We delete each cookie name across the most
  //    common path/domain combinations so kiosk leftovers can't survive.
  try {
    const names = document.cookie
      .split(";")
      .map((c) => c.trim().split("=")[0])
      .filter(Boolean);
    const host = window.location.hostname;
    const domains = ["", host, `.${host}`];
    const paths = ["/", window.location.pathname];
    for (const name of names) {
      for (const d of domains) {
        for (const p of paths) {
          const domainPart = d ? `; domain=${d}` : "";
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}${domainPart}; SameSite=Lax`;
        }
      }
    }
  } catch { /* cookie API unavailable */ }

  // 3. IndexedDB. The portal doesn't use it directly but third-party
  //    libraries (Turnstile, analytics) might. Enumerate + delete.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const idb = (window.indexedDB as unknown) as { databases?: () => Promise<{ name?: string }[]> };
    if (typeof idb.databases === "function") {
      const dbs = await idb.databases();
      for (const { name } of dbs) {
        if (name) window.indexedDB.deleteDatabase(name);
      }
    }
  } catch { /* not supported in older browsers */ }

  // 4. Service workers — uncommon for this portal but defensive.
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) await r.unregister();
    }
  } catch { /* no SW */ }
}

const OcStepDone: React.FC<OcStepDoneProps> = ({ config }) => {
  const primaryColor = config.branding.primaryColor || "#0ea5e9";
  const [cleanupState, setCleanupState] = useState<"idle" | "cleaning" | "done">("idle");
  const [canCloseHint, setCanCloseHint] = useState(false);

  async function handleCleanupAndClose() {
    setCleanupState("cleaning");
    await ocClearLocalState();
    setCleanupState("done");
    // Browsers block window.close() on tabs/windows not opened via script.
    // Try anyway; if it fails (still on the page after a brief tick), show
    // a clear "you can close this tab now" message instead of leaving the
    // user wondering whether the button did anything.
    try {
      window.close();
    } catch {
      /* security exception */
    }
    setTimeout(() => {
      if (!window.closed) setCanCloseHint(true);
    }, 300);
  }

  return (
    <div className="flex flex-col items-center text-center py-8">
      <div
        className="rounded-full p-4 mb-6"
        style={{ backgroundColor: "#dcfce7" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#16a34a"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>

      <h1
        className="text-2xl font-semibold text-slate-900 mb-3"
        style={{ color: primaryColor }}
      >
        Password reset complete
      </h1>

      <p className="text-slate-700 max-w-md leading-relaxed">
        You can now sign in with your new password. It may take a few minutes
        for the change to propagate to all systems.
      </p>

      <div className="w-full max-w-sm mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={handleCleanupAndClose}
          disabled={cleanupState !== "idle"}
          className="w-full rounded-md px-4 py-3 text-sm font-semibold text-white shadow disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
          style={{ backgroundColor: primaryColor }}
        >
          {cleanupState === "idle" && "Clean up & close this window"}
          {cleanupState === "cleaning" && "Cleaning up…"}
          {cleanupState === "done" && "All clear — you can close this tab"}
        </button>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Clears stored session data, cookies, and cached credentials from this
          browser before closing — recommended on shared or public computers.
        </p>
        {canCloseHint && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 leading-relaxed">
            Your browser blocked the automatic close (it only allows scripts to
            close tabs they opened themselves). Your session data has been
            cleared — please close this tab manually.
          </div>
        )}
      </div>
    </div>
  );
};

export default OcStepDone;
